import {PrimeIcons} from "primereact/api";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {Dropdown} from "primereact/dropdown";
import {Toast} from "primereact/toast";
import type {ChangeEvent} from "react";
import {useRef, useState} from "react";
import {useTranslation} from "react-i18next";

import {loadDocumentsAsync, uploadDocumentAsync} from "../redux/documentsThunk";
import {useRevaDispatch, useRevaSelector} from "../redux/revaHooks";
import {selectHasValueOrders} from "../redux/valueOrdersSlice";

const documentTypes = [
    "SitePlan",
    "Image",
    "InspectionProtocol",
    "FloorPlan",
    "LandRegister",
    "BuildingDescription",
    "DeclarationOfCondominium",
    "LivingSpaceCalculation",
    "LivingSpaceSurvey",
    "PropertyOwnersPowerOfAttorney",
    "OfficialCadastralMap",
    "ContaminationRecord",
    "BuildingRestrictionsRecord",
    "NonOfficialCadastralMap",
    "PartitionPlan",
    "ComparisonDocument",
    "GrossFloorAreaCalculation",
];

interface DocumentUploaderProps {
    /** optional fester Typ des hochzuladenden Dokuments */
    documentType?: string;
    /**
     * optionaler Callback nach erfolgreichem Upload — erhält die hochgeladene Datei und die vom Server vergebene
     * documentId (z.B. um eine IDP-Extraktion anzustoßen).
     */
    onUploaded?: (file: File, documentId: string) => void;
    /**
     * optionale Rückfrage direkt VOR dem Upload — erhält die gewählte Datei und den Upload-Typ. Liefert sie
     * (ggf. asynchron, z.B. nach einem Bestätigungs-Dialog) false, wird der Upload abgebrochen.
     */
    confirmBeforeUpload?: (file: File, type: string) => boolean | Promise<boolean>;
}

/**
 * Component for document upload.
 *
 * Ablauf (#18506-Folge / Upload-Redesign): Der "Hochladen"-Button öffnet direkt
 * den Datei-Dialog des Betriebssystems. Nach der Dateiwahl wird — sofern kein fester
 * documentType vorgegeben ist — in einem kleinen Dialog der Orders-API-Typ abgefragt
 * (nur OK/Abbrechen). Bei festem documentType (z.B. EnergyCertificate, LandRegister)
 * wird ohne Rückfrage direkt hochgeladen.
 */
function DocumentUploader({documentType, onUploaded, confirmBeforeUpload}: DocumentUploaderProps) {
    const {t} = useTranslation();
    const toast = useRef<Toast>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const hasValueOrders = useRevaSelector(selectHasValueOrders);

    // Bei dynamischem Typ hält pendingFile die gewählte Datei, bis der Nutzer im
    // Dialog mit OK bestätigt; type ist der dort gewählte Orders-API-Typ.
    const [pendingFile, setPendingFile] = useState<File | null>(null);
    const [type, setType] = useState<string | undefined>(undefined);

    const dispatch = useRevaDispatch();

    const doUpload = async (file: File, uploadType: string) => {
        if (confirmBeforeUpload && !(await confirmBeforeUpload(file, uploadType))) {
            return;
        }
        const documentId = await dispatch(uploadDocumentAsync(file, uploadType));
        if (typeof documentId === "string") {
            toast.current?.show({severity: "info", summary: "Info", detail: t("documents.upload.success")});
            void dispatch(loadDocumentsAsync());
            onUploaded?.(file, documentId);
        }
    };

    const onFileChosen = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        // Input zurücksetzen, damit dieselbe Datei direkt erneut gewählt werden kann.
        event.target.value = "";
        if (!file) {
            return;
        }
        if (documentType) {
            // Fester Typ -> keine Rückfrage, direkt hochladen.
            void doUpload(file, documentType);
        } else {
            // Dynamischer Typ -> Typ im Dialog abfragen.
            setType(undefined);
            setPendingFile(file);
        }
    };

    const confirmUpload = async () => {
        const file = pendingFile;
        const uploadType = type;
        setPendingFile(null);
        if (!file || !uploadType) {
            return;
        }
        await doUpload(file, uploadType);
    };

    const cancelUpload = () => {
        setPendingFile(null);
        setType(undefined);
    };

    const dialogFooter = (
        <div>
            <Button id="uploadDocumentCancel" label={t("general.cancel")} text onClick={cancelUpload} />
            <Button
                id="uploadDocumentSubmit"
                label={t("general.ok")}
                disabled={!type}
                onClick={() => void confirmUpload()}
            />
        </div>
    );

    return (
        <div>
            <input
                ref={fileInputRef}
                id="uploadDocumentFileInput"
                type="file"
                style={{display: "none"}}
                onChange={onFileChosen}
            />
            <Button
                id="uploadDocumentList"
                label={t("documents.upload.upload")}
                icon={PrimeIcons.UPLOAD}
                disabled={!hasValueOrders}
                onClick={() => fileInputRef.current?.click()}
                className={"ml-4"}
            />
            <Dialog
                visible={!!pendingFile}
                header={t("documents.upload.header")}
                onHide={cancelUpload}
                footer={dialogFooter}
            >
                <div className="flex flex-col gap-4">
                    <span>{pendingFile?.name}</span>
                    <Dropdown
                        inputId="uploadDocumentType"
                        value={type}
                        options={documentTypes}
                        placeholder={t("documents.upload.type")}
                        onChange={(e) => setType(e.value)}
                        className="w-[21rem]"
                    />
                </div>
            </Dialog>
            <Toast ref={toast} position="top-center" />
        </div>
    );
}

export default DocumentUploader;
